import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  MessageSquare, 
  ImageIcon, 
  Video,
  Mic, 
  FileText, 
  Settings, 
  Menu,
  X,
  Globe,
  History,
  Users
} from "lucide-react";
import { cn } from "../lib/utils";
import { useLanguage } from "../context/LanguageContext";

// Large Logo Component - Circular with H letter
const Logo = ({ size = 44 }) => (
  <div 
    className="relative rounded-full bg-foreground overflow-hidden"
    style={{ width: size, height: size }}
  >
    <svg width={size} height={size} viewBox="0 0 100 100">
      {/* Outer Ring */}
      <circle 
        cx="50" cy="50" r="44" 
        fill="none" 
        className="stroke-background"
        strokeWidth="1.5"
        opacity="0.3"
      />
      {/* H Letter */}
      <g className="fill-background">
        <rect x="26" y="26" width="12" height="48" rx="3" />
        <rect x="62" y="26" width="12" height="48" rx="3" />
        <rect x="26" y="44" width="48" height="12" rx="3" />
      </g>
      {/* Rotating Ring */}
      <circle 
        cx="50" cy="50" r="42" 
        fill="none" 
        className="stroke-background"
        strokeWidth="1" 
        strokeDasharray="6 10"
        opacity="0.2"
      >
        <animateTransform 
          attributeName="transform" 
          type="rotate" 
          from="0 50 50" 
          to="360 50 50" 
          dur="20s" 
          repeatCount="indefinite" 
        />
      </circle>
    </svg>
  </div>
);

export default function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t, language, setLanguage } = useLanguage();

  const navItems = [
    { path: "/chat", icon: MessageSquare, label: t("chat") },
    { path: "/assistants", icon: Users, label: t("assistants") },
    { path: "/image", icon: ImageIcon, label: t("images") },
    { path: "/video", icon: Video, label: t("video") },
    { path: "/speech", icon: Mic, label: t("voice") },
    { path: "/summarize", icon: FileText, label: t("summarize") },
    { path: "/history", icon: History, label: t("history") },
    { path: "/settings", icon: Settings, label: t("settings") },
  ];

  const isActive = (path) => {
    if (path === "/chat") return location.pathname.startsWith("/chat");
    return location.pathname === path;
  };

  return (
    <>
      {/* Mobile Header - LARGER */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border mobile-header">
        <div className="flex items-center justify-between px-4 h-full">
          {/* Logo & Name - LARGER */}
          <Link to="/chat" className="flex items-center gap-3">
            <Logo size={44} />
            <span className="text-xl font-bold text-foreground tracking-tight">HELCOO</span>
          </Link>
          
          {/* Action Buttons - LARGER */}
          <div className="flex items-center gap-2">
            {/* Language Switcher */}
            <button
              onClick={() => setLanguage(language === "ar" ? "en" : "ar")}
              className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center text-foreground"
              data-testid="mobile-language-switcher"
            >
              <Globe className="w-5 h-5" />
            </button>
            
            {/* Menu Toggle - LARGER */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center text-foreground"
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="md:hidden fixed inset-0 bg-black/40 z-40"
              onClick={() => setIsOpen(false)}
              style={{ animation: 'fadeIn 0.15s ease-out' }}
            />
            {/* Menu Panel */}
            <div
              className="md:hidden fixed top-0 right-0 bottom-0 w-64 bg-background border-l border-border z-50"
              style={{ 
                paddingTop: 'calc(70px + env(safe-area-inset-top, 0))',
                animation: 'slideIn 0.2s ease-out'
              }}
            >
              <nav className="p-4 space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-${item.path.slice(1)}`}
                  >
                    <div
                      className={cn(
                        "flex items-center gap-3 px-4 py-3.5 rounded-xl text-base",
                        isActive(item.path) 
                          ? "bg-foreground text-background font-semibold" 
                          : "text-muted-foreground"
                      )}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                  </Link>
                ))}
              </nav>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Bottom Navigation Bar - LARGER */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-t border-border mobile-bottom-nav">
        <div className="flex items-center justify-around h-full px-2">
          {navItems.slice(0, 5).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`bottom-nav-${item.path.slice(1)}`}
              className={cn(
                "flex flex-col items-center gap-1 py-2 px-3 rounded-xl transition-all min-w-[60px]",
                isActive(item.path) 
                  ? "text-foreground" 
                  : "text-muted-foreground"
              )}
            >
              <div className={cn(
                "p-2 rounded-xl transition-all",
                isActive(item.path) && "bg-secondary"
              )}>
                <item.icon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}
